import { Link } from 'react-router-dom'
import { journeyCards, testimonials, type JourneyCard } from '@/data/journeys'
import { Button } from '@/components/ui'
import { SEO, HomePageStructuredData } from '@/components/seo'

// Journey icons
function JourneyIcon({ icon }: { icon: JourneyCard['icon'] }) {
  const iconProps = {
    width: 26,
    height: 26,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: '#0a0d12',
    strokeWidth: 2,
  }

  switch (icon) {
    case 'video':
      return (
        <svg {...iconProps}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
          />
        </svg>
      )
    case 'microphone':
      return (
        <svg {...iconProps}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z"
          />
        </svg>
      )
    case 'chart':
      return (
        <svg {...iconProps}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941"
          />
        </svg>
      )
    case 'pound':
      return (
        <svg {...iconProps}>
          <circle cx="12" cy="12" r="9" strokeLinecap="round" strokeLinejoin="round" />
          <text
            x="12"
            y="16"
            textAnchor="middle"
            fontSize="12"
            fontWeight="600"
            fill="#0a0d12"
            stroke="none"
            fontFamily="Arial"
          >
            £
          </text>
        </svg>
      )
    case 'users':
      return (
        <svg {...iconProps}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
          />
        </svg>
      )
    case 'presentation':
      return (
        <svg {...iconProps}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5m.75-9l3-3 2.148 2.148A12.061 12.061 0 0116.5 7.605"
          />
        </svg>
      )
  }
}

// Color classes mapping for journey cards
const colorStyles: Record<
  JourneyCard['colorClass'],
  {
    iconBg: string
    beforeBg: string
    afterBg: string
    ctaColor: string
    accentLine: string
  }
> = {
  meeting: {
    iconBg: 'bg-gradient-to-br from-[#14b8a6] to-[#0d9488]',
    beforeBg:
      'before:bg-gradient-to-br before:from-[#14b8a6] before:via-[#0d9488] before:to-[#065f5b]',
    afterBg: 'after:bg-[radial-gradient(ellipse_at_bottom,rgba(20,184,166,0.15)_0%,transparent_60%)]',
    ctaColor: 'text-[#14b8a6]',
    accentLine: 'bg-gradient-to-r from-[#14b8a6] to-[#2dd4bf]',
  },
  ramble: {
    iconBg: 'bg-gradient-to-br from-[#8b5cf6] to-[#7c3aed]',
    beforeBg:
      'before:bg-gradient-to-br before:from-[#8b5cf6] before:via-[#7c3aed] before:to-[#5b21b6]',
    afterBg: 'after:bg-[radial-gradient(ellipse_at_bottom,rgba(139,92,246,0.15)_0%,transparent_60%)]',
    ctaColor: 'text-[#8b5cf6]',
    accentLine: 'bg-gradient-to-r from-[#8b5cf6] to-[#a78bfa]',
  },
  growth: {
    iconBg: 'bg-gradient-to-br from-[#f59e0b] to-[#d97706]',
    beforeBg:
      'before:bg-gradient-to-br before:from-[#f59e0b] before:via-[#d97706] before:to-[#b45309]',
    afterBg: 'after:bg-[radial-gradient(ellipse_at_bottom,rgba(245,158,11,0.15)_0%,transparent_60%)]',
    ctaColor: 'text-[#f59e0b]',
    accentLine: 'bg-gradient-to-r from-[#f59e0b] to-[#fbbf24]',
  },
  referral: {
    iconBg: 'bg-gradient-to-br from-[#ec4899] to-[#db2777]',
    beforeBg:
      'before:bg-gradient-to-br before:from-[#ec4899] before:via-[#db2777] before:to-[#be185d]',
    afterBg: 'after:bg-[radial-gradient(ellipse_at_bottom,rgba(236,72,153,0.15)_0%,transparent_60%)]',
    ctaColor: 'text-[#ec4899]',
    accentLine: 'bg-gradient-to-r from-[#ec4899] to-[#f472b6]',
  },
  portal: {
    iconBg: 'bg-gradient-to-br from-[#6366f1] to-[#4f46e5]',
    beforeBg:
      'before:bg-gradient-to-br before:from-[#6366f1] before:via-[#4f46e5] before:to-[#4338ca]',
    afterBg: 'after:bg-[radial-gradient(ellipse_at_bottom,rgba(99,102,241,0.15)_0%,transparent_60%)]',
    ctaColor: 'text-[#6366f1]',
    accentLine: 'bg-gradient-to-r from-[#6366f1] to-[#818cf8]',
  },
  headroom: {
    iconBg: 'bg-gradient-to-br from-[#10b981] to-[#059669]',
    beforeBg:
      'before:bg-gradient-to-br before:from-[#10b981] before:via-[#059669] before:to-[#047857]',
    afterBg: 'after:bg-[radial-gradient(ellipse_at_bottom,rgba(16,185,129,0.15)_0%,transparent_60%)]',
    ctaColor: 'text-[#10b981]',
    accentLine: 'bg-gradient-to-r from-[#10b981] to-[#34d399]',
  },
}

function JourneyCardComponent({ journey }: { journey: JourneyCard }) {
  const styles = colorStyles[journey.colorClass]

  return (
    <Link
      to={`/journey/${journey.slug}`}
      className={`
        flex-1 relative cursor-pointer overflow-hidden flex flex-col justify-end
        text-inherit no-underline will-change-[flex]
        transition-[flex] duration-[600ms] [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]
        lg:hover:flex-[1.8]
        min-h-[180px] lg:min-h-0
        before:content-[''] before:absolute before:inset-0 before:opacity-[0.12]
        before:transition-opacity before:duration-[600ms] before:[transition-timing-function:cubic-bezier(0.22,1,0.36,1)]
        hover:before:opacity-[0.22]
        after:content-[''] after:absolute after:inset-0 after:opacity-0
        after:transition-opacity after:duration-[600ms] after:[transition-timing-function:cubic-bezier(0.22,1,0.36,1)]
        after:pointer-events-none
        hover:after:opacity-100
        [&:not(:last-child)]:border-b [&:not(:last-child)]:lg:border-b-0
        [&:not(:last-child)]:lg:border-r [&:not(:last-child)]:border-white/[0.08]
        group
        ${styles.beforeBg}
        ${styles.afterBg}
      `}
    >
      {/* Content */}
      <div
        className="
          relative z-[2] px-7 py-10
          bg-gradient-to-t from-[rgba(10,13,18,0.95)] via-[rgba(10,13,18,0.7)] to-transparent
          transition-all duration-[600ms] [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]
          group-hover:pb-14
        "
      >
        {/* Icon */}
        <div
          className={`
            w-[52px] h-[52px] rounded-[14px] flex items-center justify-center mb-5
            shadow-[0_4px_12px_rgba(0,0,0,0.2)]
            transition-all duration-500 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]
            group-hover:scale-[1.08] group-hover:-translate-y-0.5
            group-hover:shadow-[0_8px_20px_rgba(0,0,0,0.3)]
            ${styles.iconBg}
          `}
        >
          <JourneyIcon icon={journey.icon} />
        </div>

        {/* Title */}
        <h3
          className="
            font-serif text-[22px] font-semibold mb-2
            transition-all duration-500 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]
            group-hover:-translate-y-0.5
          "
        >
          {journey.title}
        </h3>

        {/* Tagline */}
        <p
          className="
            text-sm text-[#9ca3af] mb-4
            transition-all duration-500 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]
            group-hover:text-[#b8bcc4]
          "
        >
          {journey.tagline}
        </p>

        {/* Description (hidden until hover on desktop, always visible on mobile) */}
        <p
          className="
            text-sm text-[#7d8490] leading-relaxed max-w-[280px]
            opacity-100 max-h-[100px] translate-y-0
            lg:opacity-0 lg:max-h-0 lg:overflow-hidden lg:translate-y-2
            transition-all duration-500 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]
            lg:group-hover:opacity-100 lg:group-hover:max-h-[100px] lg:group-hover:translate-y-0
          "
        >
          {journey.description}
        </p>

        {/* CTA */}
        <div
          className={`
            inline-flex items-center gap-2 mt-4 text-sm font-semibold
            opacity-100 translate-y-0
            lg:opacity-0 lg:translate-y-2
            transition-all duration-500 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] delay-[50ms]
            lg:group-hover:opacity-100 lg:group-hover:translate-y-0
            ${styles.ctaColor}
          `}
        >
          See the journey
          <svg
            className="w-4 h-4 transition-transform duration-300 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] group-hover:translate-x-1"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </div>
      </div>

      {/* Accent Line */}
      <div
        className={`
          absolute bottom-0 left-0 right-0 h-[3px] origin-left
          scale-x-0 transition-transform duration-[600ms] [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]
          group-hover:scale-x-100
          ${styles.accentLine}
        `}
      />
    </Link>
  )
}

function TestimonialCard({ testimonial }: { testimonial: (typeof testimonials)[0] }) {
  return (
    <div
      className="
        bg-[linear-gradient(145deg,rgba(255,255,255,0.03)_0%,rgba(255,255,255,0.01)_100%)]
        border border-white/[0.06] rounded-2xl p-7
        flex flex-col transition-all duration-300
        hover:border-[rgba(20,184,166,0.2)] hover:bg-white/[0.04]
        hover:shadow-[0_12px_40px_rgba(0,0,0,0.3)] lg:hover:scale-[1.03]
      "
    >
      {/* Stars */}
      <div className="text-star mb-3 tracking-[2px]">★★★★★</div>

      {/* Quote */}
      <p className="text-[15px] text-brand-muted leading-[1.7] mb-5 italic flex-1">
        "{testimonial.quote}"
      </p>

      {/* Author */}
      <div>
        <div className="text-sm text-brand-light font-semibold">{testimonial.author}</div>
        <div className="text-[13px] text-accent-teal">
          {testimonial.role}
          {testimonial.company && `, ${testimonial.company}`}
        </div>
        {testimonial.featured && (
          <div className="text-[11px] text-brand-subtle mt-1.5 italic">{testimonial.featured}</div>
        )}
      </div>
    </div>
  )
}

export default function HomePage() {
  return (
    <>
      <SEO
        title="WealthSpace | AI Practice Management for UK Financial Advisers"
        description="Transform your financial advice practice with WealthSpace. AI-powered suitability reports, client portals, and compliance tools. Start your free trial today."
        keywords="financial adviser software UK, AI suitability reports, practice management software, IFA software, paraplanning tools, FCA compliance software, client portal financial advisers, wealth management platform"
        url="/"
      />
      <HomePageStructuredData />

      {/* Hero */}
      <section className="pt-[100px] pb-6 px-6 max-w-[1200px] mx-auto relative z-[1] text-center">
        <p className="text-[14px] font-semibold text-accent-teal tracking-[0.1em] uppercase mb-6 opacity-0 animate-fade-in-up [animation-delay:0.1s]">
          Choose your story
        </p>
        <h1 className="font-serif text-[clamp(42px,6vw,60px)] font-normal leading-[1.15] tracking-[-0.02em] mb-6 max-w-[900px] mx-auto opacity-0 animate-fade-in-up [animation-delay:0.2s]">
          See how WealthSpace fits <span className="text-accent-teal">your</span> practice
        </h1>
        <p className="text-[19px] text-brand-muted leading-[1.7] font-normal max-w-[600px] mx-auto mb-8 opacity-0 animate-fade-in-up [animation-delay:0.3s]">
          Every adviser's journey is different. Click a story below to see how WealthSpace
          transforms the moments that matter most to you.
        </p>
      </section>

      {/* Awards & Recognition */}
      <section className="pb-12 px-6 relative z-[1]">
        <div className="max-w-[900px] mx-auto">
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4 text-[13px] text-brand-subtle">
            <div className="flex items-center gap-2">
              <span className="text-amber-500">★</span>
              <span>LangCat TechCatWalk Finalist 2025</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-white/10" />
            <div className="flex items-center gap-2">
              <span className="text-amber-500">★</span>
              <span>Sparkies FinTech of the Year Shortlist 2025</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-white/10" />
            <div className="flex items-center gap-2">
              <span className="text-amber-500">★</span>
              <span>WealthTech100 Nominee 2026</span>
            </div>
          </div>
        </div>
      </section>

      {/* Journey Selector */}
      <section className="px-6 pb-[120px] relative z-[1]">
        <div className="flex flex-col lg:flex-row lg:min-h-[500px] rounded-2xl overflow-hidden border border-white/[0.08] max-w-[1200px] mx-auto">
          {journeyCards.map((journey) => (
            <JourneyCardComponent key={journey.slug} journey={journey} />
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-6 relative z-[1]">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="font-serif text-4xl font-normal text-center text-brand-light mb-12">
            What advisers <span className="text-accent-teal italic">say</span>
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {testimonials.map((testimonial, index) => (
              <TestimonialCard key={index} testimonial={testimonial} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-[120px] px-6 max-w-[900px] mx-auto text-center relative z-[1]">
        <div className="bg-[linear-gradient(145deg,rgba(255,255,255,0.03)_0%,rgba(255,255,255,0.01)_100%)] border border-white/[0.08] rounded-3xl py-20 px-12">
          <h2 className="font-serif text-[44px] font-normal text-brand-light tracking-[-0.02em] mb-5">
            Ready to start your journey?
          </h2>
          <p className="text-lg text-brand-muted leading-[1.7] max-w-[500px] mx-auto mb-10">
            Book a 30-minute demo and we'll show you how WealthSpace fits your practice. Then try
            it yourself, free for 7 days.
          </p>
          <Button href="/get-started" size="lg">
            Get Started
            <svg
              className="w-[18px] h-[18px]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Button>
          <p className="text-sm text-brand-subtle mt-6">Free. Friendly. Absolutely no obligation.</p>
        </div>
      </section>
    </>
  )
}
